
import * as Sentry from '@sentry/react';
import React from 'react';

/**
 * Initialize Sentry with the provided configuration
 * @param dsn The DSN from your Sentry project
 * @param environment The current environment (development, production, etc.)
 */
export const initSentry = (dsn: string, environment: string = 'development') => {
  Sentry.init({
    dsn,
    environment,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set replaysSessionSampleRate to 0.1 to capture 10% of sessions
    replaysSessionSampleRate: 0.1,
    // Set replaysOnErrorSampleRate to 1.0 to capture 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  });
};

/**
 * Set user context for better error tracking
 * @param userId The ID of the currently logged in user
 * @param additionalData Additional user data to track
 */
export const setUserContext = (userId: string, additionalData?: Record<string, any>) => {
  Sentry.setUser({
    id: userId,
    ...additionalData,
  });
};

/**
 * Clear user context when user logs out
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Manually capture an exception
 * @param error The error object to capture
 * @param context Additional context about the error
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Create a wrapped component with Sentry error boundary
 * @param component The component to wrap
 * @param fallback The fallback UI to show when an error occurs
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback: React.ReactNode
) => {
  // Create a proper FallbackRender function for Sentry
  const fallbackRender: Sentry.FallbackRender = (errorData) => {
    // If fallback is a function, it needs to be called with the error and resetError
    if (typeof fallback === 'function') {
      return (fallback as any)(errorData) as React.ReactElement;
    }
    // Otherwise, render the fallback directly
    return React.createElement('div', {}, fallback);
  };

  return Sentry.withErrorBoundary(Component, {
    fallback: typeof fallback === 'function' 
      ? fallbackRender 
      : React.createElement('div', {}, fallback),
  });
};
